import type { RefObject } from 'react'
import { useState } from 'react'
import { Upload } from 'lucide-react'
import { Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { OrgReuseProofRow } from '../types'
import { ReuseProofDialog } from './ReuseProofDialog'

export const ProofUploadButton = ({
  inputRef,
  disabled,
  canUpload = true,
  onFilesSelected,
  reuseProofRows,
  onReuseProofsConfirm,
}: {
  inputRef: RefObject<HTMLInputElement>
  disabled: boolean
  /** When false, only re-use (if available) is shown — e.g. customers vs admins. */
  canUpload?: boolean
  onFilesSelected: (files: File[]) => void
  reuseProofRows?: OrgReuseProofRow[]
  onReuseProofsConfirm?: (urls: string[]) => Promise<boolean>
}) => {
  const { t } = useLocalization()
  const [reuseOpen, setReuseOpen] = useState(false)
  const showReuse =
    (reuseProofRows?.length ?? 0) > 0 && onReuseProofsConfirm != null

  return (
    <>
      <Row className="w-full flex-wrap items-start justify-end gap-2">
        {canUpload ? (
          <>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept="image/*,.pdf"
              multiple
              onChange={(e) => {
                const list = e.target.files
                if (list?.length) {
                  onFilesSelected(Array.from(list))
                  e.target.value = ''
                }
              }}
            />

            <Button
              size="sm"
              variant="secondary"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('Upload proofs', 'Ladda upp korrekturer')}
            </Button>
          </>
        ) : null}

        {showReuse ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => setReuseOpen(true)}
          >
            {t('Reuse proofs', 'Återanvänd korrekturer')}
          </Button>
        ) : null}
      </Row>

      {showReuse ? (
        <ReuseProofDialog
          open={reuseOpen}
          onOpenChange={setReuseOpen}
          rows={reuseProofRows ?? []}
          disabled={disabled}
          onConfirm={onReuseProofsConfirm}
        />
      ) : null}
    </>
  )
}
